import {
  forwardRef,
  useState,
  ReactElement,
  useMemo,
  useEffect,
  useRef,
} from 'react';

import { cn } from 'utils/helpers';
import { BsInfoCircle } from 'react-icons/bs';
import { SIZE } from 'utils/constants';
import { cva } from 'class-variance-authority';
import type { IconType } from 'react-icons';
import {
  DecoupledEditor,
  EditorConfig,
  Essentials,
  Heading,
  Mention,
  MentionConfig,
} from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { size as _size } from 'lodash';

import AttachFile from '../Textarea/CustomPlugins/AttachFile';
import Tooltip from '../../common/Tooltip';
import './style.css';

export type PROPS = {
  name?: string;
  type?: 'text' | 'password' | 'email' | 'number';
  size?: SIZE | 'default';
  textType?: 'default' | 'danger' | 'success' | 'warning';
  label?: string;
  labelSubText?: string;
  subText?: string;
  required?: boolean;
  information?: string;
  isAmount?: boolean;
  hideNumberStepper?: boolean;
  addon?: {
    icon: IconType;
    onClick?: () => void;
    tooltip?: string;
    className?: string;
  };
  icon?: ReactElement;
  classOverrides?: {
    input?: string | string[];
    subtext?: string | string[];
  };
  mentionItems?: MentionConfig;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
};

const inputClasses = cva(
  [
    'bg-fill-tertiary text-sm',
    'focus:outline-none focus:border-separator-secondary',
    'disabled:placeholder-disabled disabled:text-disabled disabled:border-disabled',
    'p-[14px] rounded',
    'border border-separator-secondary',
  ],
  {
    variants: {
      size: {
        default: ['max-h-[36px]', 'py-2 h-9', 'rounded'],
        [SIZE.Small]: ['max-h-[36px]', 'px-3 py-2 h-9', 'rounded'],
        [SIZE.Medium]: ['max-h-[44px]', 'p-3 h-[44px]', 'rounded-[6px]'],
        [SIZE.Large]: [
          'max-h-[52px]',
          'px-[14px] py-[18px] h-[52px]',
          'rounded-lg',
          'text-base',
        ],
      },
      textType: {
        default: ['placeholder-content', 'focus:border-primary-base'],
        success: [
          'placeholder-primary-base text-primary-base',
          '!border-primary-base',
          '!focus:border-primary-base',
        ],
        danger: [
          'placeholder-danger-base text-danger-base',
          '!border-danger-base',
          '!focus:border-danger-base',
        ],
        warning: [
          'placeholder-warning-base text-warning-base',
          '!border-warning-base',
          '!focus:border-warning-base',
        ],
      },
      isFocused: {
        true: ['border-primary-base'],
      },
    },
    defaultVariants: { size: 'default', textType: 'default' },
  },
);

const subTextClasses = cva(['text-xs'], {
  variants: {
    textType: {
      default: [],
      success: ['text-primary-base'],
      danger: ['text-danger-base'],
      warning: ['text-warning-base'],
    },
    size: {
      default: 'text-xs',
      [SIZE.Small]: 'text-xs',
      [SIZE.Medium]: 'text-xs',
      [SIZE.Large]: 'text-sm',
    },
  },
  defaultVariants: {
    textType: 'default',
  },
});

const MentionableInput = forwardRef(
  ({
    size,
    textType,
    label,
    labelSubText = '(Optional)',
    subText,
    required = false,
    information,
    classOverrides,
    placeholder,
    mentionItems,
    disabled,
    value,
    onChange,
    onBlur,
    name = '',
  }: PROPS) => {
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const editorRef = useRef<any>(null);

    useEffect(() => {
      setIsLayoutReady(true);

      return () => setIsLayoutReady(false);
    }, []);

    const { editorConfig } = useMemo<{ editorConfig: EditorConfig }>(() => {
      if (!isLayoutReady) {
        return { editorConfig: {} };
      }
      const LICENSE_KEY = 'GPL';
      return {
        editorConfig: {
          licenseKey: LICENSE_KEY,
          plugins: [Essentials, Heading, Mention],
          extraPlugins: [AttachFile],
          initialData: '',
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
              toggleDownloadable: {
                mode: 'manual',
                label: 'Downloadable',
                attributes: {
                  download: 'file',
                },
              },
            },
          },
          list: {
            properties: {
              styles: true,
              startIndex: true,
              reversed: true,
            },
          },
          mention: mentionItems,
          placeholder,
        },
      };
    }, [isLayoutReady, placeholder, mentionItems]);

    useEffect(() => {
      return () => {
        if (editorRef.current) {
          editorRef.current.destroy();
          editorRef.current = null;
        }
      };
    }, []);

    return (
      <div className={cn('space-y-2.5', 'w-full')}>
        {(label || information) && (
          <div className="flex items-center justify-between h-5">
            {label && (
              <label htmlFor={name}>
                <span className="mr-1 text-sm font-semibold">{label}</span>
                {required ? (
                  <span className="text-danger-base">*</span>
                ) : (
                  <span className="text-xs text-content">{labelSubText}</span>
                )}
              </label>
            )}
            {!!information && (
              <Tooltip placement="right" title={information} theme="dark">
                <div className="input-info">
                  <BsInfoCircle />
                </div>
              </Tooltip>
            )}
          </div>
        )}
        <div
          className={cn(
            inputClasses({
              size,
              textType,
              isFocused: isFocused || isHovered,
            }),
            'break-all',
          )}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => !disabled && setIsHovered(false)}
        >
          {_size(editorConfig) && (
            <div className={cn('relative mention-v2', size)}>
              <CKEditor
                editor={DecoupledEditor}
                data={value}
                onChange={(_, editor) => {
                  if (editor) {
                    onChange?.(editor.getData() as string);
                  }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={(_, editor) => {
                  setIsFocused(false);
                  onBlur?.(editor.getData() as string);
                }}
                config={{
                  ...editorConfig,
                }}
                disabled={disabled}
                onReady={(editor) => {
                  editorRef.current = editor;

                  // 🔹 Limit to single line
                  editor.editing.view.document.on('enter', (evt) => {
                    evt.stop();
                  });

                  // 🔹 Prevent soft breaks
                  editor.commands
                    .get('shiftEnter')
                    ?.forceDisabled('no-newlines');

                  // 🔹 Remove default margins in the view
                  if (editor) {
                    const root = editor.editing.view.document.getRoot();
                    if (root) {
                      const view = editor.editing.view;
                      view.change((writer: any) => {
                        writer.setAttribute(
                          'style',
                          'margin:0;padding:0;',
                          root,
                        );
                      });
                    }
                  }
                }}
              />
            </div>
          )}
        </div>
        {subText && (
          <div
            className={cn(
              subTextClasses({ textType, size }),
              classOverrides?.subtext,
            )}
          >
            {subText}
          </div>
        )}
      </div>
    );
  },
);

export default MentionableInput;
